<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoney } from '@/utils/formatMoney'
import { defaultLocaleForDate } from '@/config/date'
import { formatDateTime } from '@/utils/formatDateTime'
import { useSubscriptionTypeCatalog } from '@/composables/useSubscriptionTypeCatalog'
import type { MergedRow } from '@/composables/useAlignedClientStreams'

/**
 * Full-row event cell for the client card visits table. Selected by
 * `DynamicTable.rowOverride` whenever a `MergedRow.kind` is
 * `balance-event`, `disease-event` or `subscription-event`. Renders one `<td colspan>` so the
 * event message spans every column (Date / Type / Δ / Bal.):
 *
 * - `balance-event`  – "Balance — &lt;amount&gt;" with the snapshot amount
 *   formatted by `formatMoney`. The italic muted styling matches the mock's
 *   `.row-event` rows so users can tell at a glance the row is informational
 *   rather than a transaction.
 * - `disease-event`  – "Disease — &lt;notes&gt; (&lt;from&gt; → &lt;to&gt;)".
 *   The from/to dates are formatted in UTC because the boundaries from the
 *   backend are midnight-UTC (mirrors `DiseasesTab`'s formatter).
 *
 * `row.date` is rendered first (mono, muted) so the event still aligns with
 * the date column above it — keeping the row visually parsable without
 * depending on the full sub-cell grid.
 */
const props = defineProps<{
  /** Merged row produced by `useAlignedClientStreams`. */
  row: MergedRow
  /** Total number of columns in the table; passed by `DynamicTable.rowOverride`. */
  colspan: number
}>()

const { t } = useI18n()

/**
 * Formats an ISO datetime as a UTC, locale-aware, date-only label.
 * Mirrors `DiseasesTab.formatDiseaseDate` so disease ranges render the
 * same way in both surfaces. Returns the empty string when the input
 * cannot be parsed; callers fall back to a generic label.
 */
function formatUtcDate(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.trim() === '') return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(defaultLocaleForDate.value, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(d)
}

const { types: subscriptionTypes } = useSubscriptionTypeCatalog()
const isPaymentNote = computed(() => props.row.kind === 'payment-subscription')
const isLessonNote = computed(() => props.row.kind === 'lesson-subscription')
const lessonNoteCounter = computed(() =>
  props.row.subscriptionUsed !== undefined && props.row.subscriptionTotal != null
    ? `${props.row.subscriptionUsed}/${props.row.subscriptionTotal}`
    : null,
)
const paymentNoteText = computed(() => {
  const name =
    subscriptionTypes.value.find((x) => x.id === props.row.subscriptionTypeId)?.name ?? ''
  return t('clientCard.subscriptionPaymentTag', { name })
})

const isSubscriptionEvent = computed(() => props.row.kind === 'subscription-event')
const isExhausted = computed(() => props.row.subscriptionEvent === 'exhausted')

/* Subscription bands sit in the ledger's own time zone like the date column
 * (visit and payment times); the other events keep their UTC day boundaries. */
const dateLabel = computed(() =>
  isSubscriptionEvent.value
    ? formatDateTime({ date: props.row.date, dateStyle: 'medium' }).formattedDate
    : formatUtcDate(props.row.date),
)

const message = computed(() => {
  if (props.row.kind === 'balance-event') {
    if (typeof props.row.balanceAmountMinor !== 'number') return t('clientCard.eventBalance')
    const amount = formatMoney({
      amount: props.row.balanceAmountMinor,
    }).formatted
    return t('clientCard.eventBalanceWithAmount', { amount })
  }
  if (props.row.kind === 'subscription-event') {
    const name = props.row.subscriptionName ?? ''
    const total = props.row.subscriptionTotal
    const used = props.row.subscriptionUsed
    // The plan's price is charged to the balance when the band is reached.
    const charge =
      typeof props.row.deltaMinor === 'number'
        ? ` (${formatMoney({ amount: props.row.deltaMinor }).formatted})`
        : ''
    if (props.row.subscriptionEvent === 'exhausted') {
      return (
        t('clientCard.eventSubscriptionExhausted', {
          name,
          used: used ?? total ?? '',
          total: total ?? '',
        }) + charge
      )
    }
    if (total == null || used == null) {
      return t('clientCard.eventSubscriptionExpiredUnlimited', { name }) + charge
    }
    return t('clientCard.eventSubscriptionExpired', { name, used, total }) + charge
  }
  if (props.row.kind === 'disease-event') {
    const from = formatUtcDate(props.row.diseaseFromDate)
    const to = formatUtcDate(props.row.date)
    const range = from && to ? ` (${from} → ${to})` : ''
    const notes =
      typeof props.row.diseaseNotes === 'string' && props.row.diseaseNotes.trim() !== ''
        ? props.row.diseaseNotes.trim()
        : t('clientCard.eventDiseaseAbsentNotes')
    return t('clientCard.eventDisease', { notes, range })
  }
  return ''
})
</script>

<template>
  <td
    v-if="isPaymentNote || isLessonNote"
    class="event-row-cell font-tufte event-row-cell--payment-note"
    :colspan="props.colspan"
  >
    <span class="event-row-cell__tag"
      ><template v-if="isPaymentNote">{{ paymentNoteText }}</template
      ><template v-else
        >{{ t('clientCard.subscriptionTag')
        }}<b v-if="lessonNoteCounter" class="event-row-cell__tag-n">
          {{ lessonNoteCounter }}</b
        ></template
      ></span
    >
  </td>
  <td
    v-else
    class="event-row-cell font-tufte"
    :class="{
      'event-row-cell--subscription': isSubscriptionEvent,
      'event-row-cell--warn': isSubscriptionEvent && isExhausted,
    }"
    :colspan="props.colspan"
  >
    <span class="event-row-cell__date">{{ dateLabel }}</span>
    <span class="event-row-cell__message">{{ message }}</span>
  </td>
</template>

<style scoped>
.event-row-cell {
  /*
   * Slightly tinted background mirrors the mock's `.row-event` rows so the
   * event reads as a horizontal break inside the ledger. `color-mix` keeps
   * the tint scaled to the current theme rather than hard-coding `#f5f5ee`.
   */
  background: color-mix(in srgb, var(--color-tufte-text) 4%, transparent);
  border-bottom: 1px solid var(--color-tufte-border);
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
}

.event-row-cell--payment-note {
  /* Full-width note under a payment or visit: no band, just the tag at the row's right edge,
   * so a long plan name stays on one line instead of wrapping inside a narrow column. */
  background: transparent;
  padding: 0 0.85rem 0.45rem 0.3rem;
  text-align: right;
}

.event-row-cell__tag {
  display: inline-block;
  background: var(--color-subscription-band);
  color: var(--color-subscription-ink);
  font-family: var(--font-tufte-sans);
  font-size: calc(var(--text-tufte-xs) - 1px);
  letter-spacing: 0.02em;
  padding: 0.05rem 0.34rem;
}

.event-row-cell__tag-n {
  font-family: var(--font-tufte-mono);
  font-weight: 600;
  margin-left: 0.3em;
}

.event-row-cell--subscription {
  /* Subscription bands use the soft green of the subscription tag. */
  background: var(--color-subscription-band);
  border-bottom-color: color-mix(
    in srgb,
    var(--color-subscription-ink) 20%,
    var(--color-tufte-border)
  );
}

.event-row-cell--subscription .event-row-cell__date {
  color: color-mix(in srgb, var(--color-subscription-ink) 80%, var(--color-tufte-muted));
}

.event-row-cell--subscription .event-row-cell__message {
  font-style: normal;
  color: var(--color-subscription-ink);
}

.event-row-cell--warn .event-row-cell__message {
  /* Quota used up: amber, as in the sketch. */
  color: #8a5a1a;
}

.event-row-cell__date {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-right: 0.6rem;
}

.event-row-cell__message {
  font-style: italic;
  color: var(--color-tufte-muted);
  font-size: var(--text-tufte-sm);
}
</style>
